import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import clinicAPI from "../../../services/clinicApi";
import { Input } from "../../ui";
import s from "../clinic.module.css";

function useDropdownPosition(anchorRef, open) {
  const [style, setStyle] = useState(null);

  const update = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const maxH = 240;
    const gap = 4;
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    const openUp = below < 120 && above > below;

    setStyle({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(maxH, openUp ? above : below),
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return style;
}

function formatNameParts(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/);
  const surname = parts[0] || "";
  const rest = parts.slice(1).join(" ");
  return { surname, rest };
}

function NameLabel({ fullName }) {
  const { surname, rest } = formatNameParts(fullName);
  return (
    <span className={s.clientSearchPickerName}>
      <strong>{surname}</strong>
      {rest ? ` ${rest}` : ""}
    </span>
  );
}

export default function ClientSearchPicker({
  value,
  onChange,
  placeholder,
  className = "",
  disabled = false,
  /** Поиск по всему клубу (для тренера при добавлении в свою группу) */
  enrollment = false,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");

  useEffect(() => {
    if (!value) {
      setQ("");
      return;
    }
    let cancelled = false;
    clinicAPI
      .getPatient(value)
      .then((r) => {
        if (!cancelled) setQ(r.patient?.full_name || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [value]);

  const fetchResults = useCallback(
    (query) => {
      setLoading(true);
      clinicAPI
        .searchPatients(query, 8, { enrollment })
        .then((r) => setResults(r.patients || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    },
    [enrollment],
  );

  useEffect(() => {
    if (!open) return undefined;
    if (q.length >= 2) {
      const t = setTimeout(() => fetchResults(q), 200);
      return () => clearTimeout(t);
    }
    if (q.length === 0) {
      setHint("Клиенты по фамилии");
      fetchResults("");
      return undefined;
    }
    setResults([]);
    setHint("");
    return undefined;
  }, [q, open, fetchResults]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = useCallback(
    (p) => {
      onChange(p.id);
      setQ(p.full_name || "");
      setOpen(false);
      setResults([]);
    },
    [onChange],
  );

  const showDropdown = open && (loading || results.length > 0 || q.length >= 2 || q.length === 0);
  const menuStyle = useDropdownPosition(rootRef, showDropdown);

  const menu =
    showDropdown && menuStyle ? (
      <ul
        ref={menuRef}
        className={s.clientSearchPickerDropdown}
        role="listbox"
        style={menuStyle}
      >
        {hint && !loading && results.length > 0 ? (
          <li className={s.clientSearchPickerHint}>{hint}</li>
        ) : null}
        {loading ? (
          <li className={s.clientSearchPickerHint}>Поиск…</li>
        ) : results.length === 0 ? (
          <li className={s.clientSearchPickerHint}>
            {q.length >= 2 ? "Никого не найдено" : "Нет клиентов"}
          </li>
        ) : (
          results.map((p) => (
            <li key={p.id} role="option" aria-selected={value === p.id}>
              <button type="button" onClick={() => pick(p)}>
                <NameLabel fullName={p.full_name} />
                {p.phone ? (
                  <span className={s.clientSearchPickerPhone}>{p.phone}</span>
                ) : null}
              </button>
            </li>
          ))
        )}
      </ul>
    ) : null;

  return (
    <div
      ref={rootRef}
      className={`${s.clientSearchPicker} ${className}`.trim()}
    >
      <Input
        value={q}
        disabled={disabled}
        onChange={(e) => {
          if (disabled) return;
          setQ(e.target.value);
          setOpen(true);
          if (!e.target.value) onChange("");
        }}
        onFocus={() => {
          if (!disabled) setOpen(true);
        }}
        placeholder={placeholder || "Фамилия, имя или телефон"}
        autoComplete="off"
      />
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
